<template>
  <div>
    <div class="flex items-center space-x-4">
      <router-link
        :to="{ name: isEditing ? 'couponsDetail' : 'couponsIndex' }"
        class="system-button"
      >
        <arrow-icon class="w-4 rotate-90"></arrow-icon>
      </router-link>
      <h2>{{ isEditing ? "Editar" : "Crear" }} Cupón</h2>
    </div>

    <div v-if="!loading" class="card mt-2">
      <form @submit.prevent="save">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="flex flex-col">
            <label for="code">Código</label>
            <input
              id="code"
              type="text"
              v-model="form.code"
              class="font-mono uppercase"
              :disabled="isEditing"
            />
            <validation-errors :errors="errorFor('code')"></validation-errors>
          </div>

          <div class="flex flex-col">
            <label for="name">Nombre</label>
            <input id="name" type="text" v-model="form.name" />
            <validation-errors :errors="errorFor('name')"></validation-errors>
          </div>
        </div>

        <div class="flex flex-col w-full mt-4">
          <label for="description">Descripción</label>
          <textarea
            id="description"
            class="h-20"
            v-model="form.description"
          ></textarea>
          <validation-errors
            :errors="errorFor('description')"
          ></validation-errors>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div class="flex flex-col">
            <label for="discount_type">Tipo de descuento</label>
            <select id="discount_type" v-model="form.discount_type">
              <option value="percentage">Porcentaje (%)</option>
              <option value="fixed">Monto fijo ($)</option>
            </select>
            <validation-errors
              :errors="errorFor('discount_type')"
            ></validation-errors>
          </div>

          <div class="flex flex-col">
            <label for="discount_value">Valor del descuento</label>
            <input
              id="discount_value"
              type="number"
              step="any"
              min="0"
              v-model="form.discount_value"
            />
            <validation-errors
              :errors="errorFor('discount_value')"
            ></validation-errors>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div class="flex flex-col">
            <label for="max_uses">Usos máx. (total)</label>
            <input
              id="max_uses"
              type="number"
              min="1"
              v-model="form.max_uses"
              placeholder="Sin límite"
            />
            <validation-errors
              :errors="errorFor('max_uses')"
            ></validation-errors>
          </div>

          <div class="flex flex-col">
            <label for="max_uses_per_client">Usos máx. por cliente</label>
            <input
              id="max_uses_per_client"
              type="number"
              min="1"
              v-model="form.max_uses_per_client"
            />
            <validation-errors
              :errors="errorFor('max_uses_per_client')"
            ></validation-errors>
          </div>

          <div class="flex flex-col">
            <label for="max_uses_per_email">Usos máx. por email</label>
            <input
              id="max_uses_per_email"
              type="number"
              min="1"
              v-model="form.max_uses_per_email"
            />
            <validation-errors
              :errors="errorFor('max_uses_per_email')"
            ></validation-errors>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div class="flex flex-col">
            <label for="valid_from">Vigente desde</label>
            <input
              id="valid_from"
              type="datetime-local"
              v-model="form.valid_from"
            />
            <validation-errors
              :errors="errorFor('valid_from')"
            ></validation-errors>
          </div>

          <div class="flex flex-col">
            <label for="valid_until">Vigente hasta</label>
            <input
              id="valid_until"
              type="datetime-local"
              v-model="form.valid_until"
            />
            <validation-errors
              :errors="errorFor('valid_until')"
            ></validation-errors>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 items-center">
          <div class="flex flex-col">
            <label for="applies_to">Aplica a</label>
            <select id="applies_to" v-model="form.applies_to">
              <option value="all">Todo</option>
              <option value="products">Productos</option>
              <option value="plans">Planes</option>
            </select>
            <validation-errors
              :errors="errorFor('applies_to')"
            ></validation-errors>
          </div>

          <div class="flex flex-col mt-2 space-y-2">
            <div class="flex items-center">
              <input
                id="is_active"
                type="checkbox"
                class="h-5 w-5"
                v-model="form.is_active"
              />
              <label for="is_active" class="ml-2">Activo</label>
            </div>

            <div class="flex items-center">
              <input
                id="first_purchase_only"
                type="checkbox"
                class="h-5 w-5"
                v-model="form.first_purchase_only"
              />
              <label for="first_purchase_only" class="ml-2"
                >Solo primera compra</label
              >
            </div>
          </div>
        </div>

        <div class="flex flex-col mt-4">
          <label>Planes aplicables</label>
          <p class="text-sm text-content-400">
            Frecuencias de pago en las que el cupón es válido (solo aplica a
            planes; no afecta compras en tienda).
          </p>
          <div class="mt-2 space-y-2">
            <div class="flex items-center">
              <input
                id="all_payment_frequencies"
                type="checkbox"
                class="h-5 w-5"
                :checked="allPaymentFrequencies"
                :disabled="allPaymentFrequencies"
                @change="form.payment_frequency_ids = []"
              />
              <label for="all_payment_frequencies" class="ml-2"
                >Todas las frecuencias</label
              >
            </div>
            <div
              v-for="frequency in selectablePaymentFrequencies"
              :key="frequency.id"
              class="flex items-center"
            >
              <input
                :id="`payment_frequency_${frequency.id}`"
                type="checkbox"
                class="h-5 w-5"
                :value="frequency.id"
                v-model="form.payment_frequency_ids"
              />
              <label :for="`payment_frequency_${frequency.id}`" class="ml-2">{{
                frequency.name
              }}</label>
            </div>
          </div>
          <validation-errors
            :errors="errorFor('payment_frequency_ids')"
          ></validation-errors>
        </div>

        <primary-button
          type="submit"
          class="mt-6 mx-0"
          :loading="loadingForm"
          >{{ isEditing ? "Guardar" : "Crear" }}</primary-button
        >
      </form>
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import validationErrors from "@/mixins/validationErrors";
import { is422 } from "@/utils/response";
export default {
  name: "Coupon Form",

  data() {
    return {
      form: {
        code: "",
        name: "",
        description: "",
        discount_type: "percentage",
        discount_value: 0,
        max_uses: null,
        max_uses_per_client: 1,
        max_uses_per_email: 1,
        valid_from: null,
        valid_until: null,
        is_active: true,
        applies_to: "all",
        first_purchase_only: false,
        payment_frequency_ids: [],
      },
      paymentFrequencies: [],
      loading: false,
      loadingForm: false,
      isEditing: false,
    };
  },

  mixins: [validationErrors],

  computed: {
    // Hide the inactive "Cortesía" frequency (active=0) — it is not a real,
    // chargeable cadence and a coupon must never be restricted to it.
    selectablePaymentFrequencies() {
      return (this.paymentFrequencies || []).filter(
        (frequency) => Number(frequency.active) !== 0
      );
    },

    // Empty selection = the coupon applies to every payment frequency.
    allPaymentFrequencies() {
      return this.form.payment_frequency_ids.length === 0;
    },
  },

  created() {
    this.$store.commit("setStoreSelectedTab", "coupons");

    this.loadPaymentFrequencies();

    if (this.$route.params.id) {
      this.isEditing = true;
      this.loadCoupon();
    }
  },

  methods: {
    async loadPaymentFrequencies() {
      try {
        const response = await api.resources.getFrequencies();
        this.paymentFrequencies = response.payment_frequencies || [];
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async loadCoupon() {
      this.loading = true;

      try {
        const coupon = await api.coupons.getById(this.$route.params.id);
        this.form = {
          code: coupon.code,
          name: coupon.name,
          description: coupon.description || "",
          discount_type: coupon.discount_type,
          discount_value: coupon.discount_value,
          max_uses: coupon.max_uses,
          max_uses_per_client: coupon.max_uses_per_client,
          max_uses_per_email: coupon.max_uses_per_email,
          valid_from: this.toLocalInput(coupon.valid_from),
          valid_until: this.toLocalInput(coupon.valid_until),
          is_active: !!coupon.is_active,
          applies_to: coupon.applies_to || "all",
          first_purchase_only: !!coupon.first_purchase_only,
          payment_frequency_ids: (coupon.payment_frequency_ids || []).map(Number),
        };
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    toLocalInput(datetime) {
      if (!datetime) return null;
      const d = new Date(datetime);
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },

    save() {
      if (!this.validateForm()) {
        this.$store.dispatch(
          "errorSnack",
          "Favor de llenar todos los campos correctamente."
        );
        return;
      }

      if (!this.isEditing) {
        this.createCoupon();
      } else {
        this.updateCoupon();
      }
    },

    validateForm() {
      this.errors = {};

      if (!this.form.code) this.errors["code"] = ["Campo requerido"];
      if (!this.form.name) this.errors["name"] = ["Campo requerido"];
      if (!this.form.discount_type)
        this.errors["discount_type"] = ["Campo requerido"];
      if (this.form.discount_value === null || this.form.discount_value === "")
        this.errors["discount_value"] = ["Campo requerido"];

      return Object.keys(this.errors).length === 0;
    },

    payload() {
      const out = { ...this.form };
      out.code = (out.code || "").toUpperCase();
      if (out.max_uses === "") out.max_uses = null;
      return out;
    },

    async createCoupon() {
      this.loadingForm = true;

      try {
        const response = await api.coupons.create(this.payload());
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({ name: "couponsIndex" });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },

    async updateCoupon() {
      this.loadingForm = true;

      try {
        const response = await api.coupons.update(
          this.$route.params.id,
          this.payload()
        );
        this.$store.dispatch("successSnack", response.message);
        this.$router.push({
          name: "couponsDetail",
          params: { id: this.$route.params.id },
        });
      } catch (error) {
        if (is422(error)) {
          this.errors = error.response && error.response.data.errors;
        }
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingForm = false;
    },
  },
};
</script>
